import markdownit from "markdown-it";

type ConversationMessage = {
  role: "user" | "assistant";
  content: string;
};

function initSearch(): void {
  // ── DOM refs ────────────────────────────────────────────────────────────
  // Resolved first so the per-element guard works correctly.
  const pageEl = document.getElementById("sv2-page") as HTMLElement;
  const emptyEl = document.getElementById("sv2-empty") as HTMLElement;
  const threadEl = document.getElementById("sv2-thread") as HTMLElement;
  const textareaEl = document.getElementById("sv2-textarea") as HTMLTextAreaElement;
  const sendBtn = document.getElementById("sv2-send") as HTMLButtonElement;

  if (!pageEl || !emptyEl || !threadEl || !textareaEl || !sendBtn) {
    return;
  }

  // Guard per DOM element, not globally.
  // A global flag breaks when React Strict Mode causes HTMX to swap the
  // response twice — new DOM nodes have no listeners but the flag stops re-init.
  if (pageEl.dataset.sv2Init) {
    return;
  }
  pageEl.dataset.sv2Init = "1";

  // ── Markdown setup ──────────────────────────────────────────────────────
  const md = markdownit({html: false, linkify: true, breaks: true});
  const defaultLinkOpen =
    md.renderer.rules.link_open ||
    // oxlint-disable-next-line typescript-eslint/no-explicit-any
    function (tokens: any, idx: any, options: any, _env: any, self: any) {
      return self.renderToken(tokens, idx, options);
    };
  // oxlint-disable-next-line typescript-eslint/no-explicit-any
  md.renderer.rules.link_open = function (tokens: any, idx: any, options: any, env: any, self: any) {
    tokens[idx].attrSet("target", "_blank");
    return defaultLinkOpen(tokens, idx, options, env, self);
  };

  // ── State ───────────────────────────────────────────────────────────────
  const conversation: Array<ConversationMessage> = [];
  const conversationId = crypto.randomUUID();
  let submitting = false;

  // API URLs are embedded server-side to handle cross-origin (Next.js vs Express)
  // oxlint-disable-next-line typescript-eslint/no-non-null-assertion
  const INQUIRE_URL = pageEl.dataset.inquire!;
  // oxlint-disable-next-line typescript-eslint/no-non-null-assertion
  const STREAM_URL = pageEl.dataset.stream!;

  // ── Textarea auto-resize ─────────────────────────────────────────────────
  function syncTextarea(): void {
    textareaEl.style.height = "auto";
    textareaEl.style.height = Math.min(textareaEl.scrollHeight, 200) + "px";
    sendBtn.disabled = !textareaEl.value.trim() || submitting;
  }

  textareaEl.addEventListener("input", syncTextarea);

  textareaEl.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!sendBtn.disabled) {
        submit();
      }
    }
  });

  sendBtn.addEventListener("click", () => submit());

  // ── Submit ────────────────────────────────────────────────────────────────
  // overrideText is used when a widget submits a synthetic user message —
  // we want it in the conversation history but no user bubble in the UI
  // (the locked widget itself is the visual record of what was submitted).
  function submit(overrideText?: string): void {
    const text = overrideText ?? textareaEl.value.trim();
    if (!text || submitting) {
      return;
    }

    submitting = true;
    if (!overrideText) {
      textareaEl.value = "";
      textareaEl.style.height = "";
    }
    sendBtn.disabled = true;

    // Transition from empty state to thread
    if (emptyEl.style.display !== "none") {
      emptyEl.style.display = "none";
      threadEl.style.display = "";
    }

    conversation.push({role: "user", content: text});
    if (!overrideText) {
      appendUserMessage(text);
    }

    const assistantEl = createAssistantPlaceholder();
    scrollToBottom();

    fetch(INQUIRE_URL, {
      method: "POST",
      headers: {"Content-Type": "application/json"},
      credentials: "include",
      body: JSON.stringify({messages: conversation, conversationId}),
    })
      .then((r) => {
        if (!r.ok) {
          throw new Error(`HTTP ${r.status}`);
        }
        return r.json() as Promise<{streamId: string}>;
      })
      .then(({streamId}) => {
        startStream(streamId, assistantEl);
      })
      .catch((err) => {
        console.error("search: inquiry failed", err);
        setStreamError(assistantEl, "Could not connect. Please try again.");
        finishSubmit();
      });
  }

  // ── DOM helpers ───────────────────────────────────────────────────────────
  function appendUserMessage(text: string): void {
    const el = document.createElement("div");
    el.className =
      "self-end max-w-[90%] sm:max-w-[78%] px-4 py-3 bg-slate-200 text-slate-900 rounded-[18px_18px_4px_18px] text-[15px] leading-[1.45] whitespace-pre-wrap break-words";
    el.textContent = text;
    threadEl.appendChild(el);
  }

  function createAssistantPlaceholder(): HTMLDivElement {
    const wrap = document.createElement("div");
    wrap.className = "flex min-w-0 flex-col gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3";
    wrap.innerHTML = `
      <div class="text-sm font-medium text-gray-900 flex items-baseline gap-2">Jess <span class="text-[10px] font-mono uppercase tracking-[0.12em] text-gray-400 font-normal">Search assistant</span></div>
      <div class="sv2-ai-status flex items-center gap-2 text-[13px] text-gray-500 italic min-h-5"><div class="w-1.5 h-1.5 rounded-full bg-scraye-orange animate-pulse shrink-0"></div><span class="sv2-status-text">Understanding your request…</span></div>
      <div class="sv2-ai-widgets min-w-0 overflow-hidden empty:hidden"></div>
      <div class="sv2-ai-text sv2-md text-[15px] leading-[1.6] text-gray-900 break-words empty:hidden" data-raw=""></div>
      <div class="sv2-ai-timings"></div>
    `;
    threadEl.appendChild(wrap);
    return wrap;
  }

  function setStatusText(container: HTMLDivElement, text: string): void {
    const statusEl = container.querySelector<HTMLElement>(".sv2-ai-status");
    if (!statusEl) {
      return;
    }
    if (!text) {
      statusEl.style.display = "none";
      return;
    }
    statusEl.style.display = "";
    const textEl = container.querySelector<HTMLElement>(".sv2-status-text");
    if (textEl) {
      textEl.textContent = text;
    }
  }

  function setStreamError(container: HTMLDivElement, message: string): void {
    const statusEl = container.querySelector<HTMLElement>(".sv2-ai-status");
    if (statusEl) {
      statusEl.innerHTML = `<span class="text-red-700 text-[13px]">${message}</span>`;
    }
  }

  function finishSubmit(): void {
    submitting = false;
    sendBtn.disabled = !textareaEl.value.trim();
  }

  // ── EventSource stream ────────────────────────────────────────────────────
  function startStream(streamId: string, container: HTMLDivElement): void {
    const es = new EventSource(`${STREAM_URL}?id=${encodeURIComponent(streamId)}`, {withCredentials: true});
    let lastSeq = 0;
    let closed = false;

    // oxlint-disable-next-line typescript-eslint/no-non-null-assertion
    const textEl = container.querySelector<HTMLElement>(".sv2-ai-text")!;
    // oxlint-disable-next-line typescript-eslint/no-non-null-assertion
    const widgetsEl = container.querySelector<HTMLElement>(".sv2-ai-widgets")!;
    // oxlint-disable-next-line typescript-eslint/no-non-null-assertion
    const timingsEl = container.querySelector<HTMLElement>(".sv2-ai-timings")!;

    function close(): void {
      if (closed) {
        return;
      }
      closed = true;
      es.close();
    }

    // Status events carry a plain string key (e.g. "thinking", "location", "results")
    es.addEventListener("status", (e: Event) => {
      setStatusText(container, (e as MessageEvent).data as string);
      scrollToBottom();
    });

    // Inquiry events carry sequenced JSON with text deltas
    es.addEventListener("inquiry", (e: Event) => {
      try {
        const msg = JSON.parse((e as MessageEvent).data as string);
        const seq: number = typeof msg.sequence === "number" ? msg.sequence : 0;

        // Deduplicate — drop stale or duplicate messages
        if (seq !== 0 && seq <= lastSeq) {
          return;
        }
        lastSeq = seq;

        if (msg.data?.type === "text-delta") {
          const current = textEl.dataset.raw ?? "";
          const next = current + (msg.data.text as string);
          textEl.dataset.raw = next;
          textEl.innerHTML = md.render(next);
          scrollToBottom();
        }
      } catch (err) {
        console.error("search: inquiry parse error", err);
      }
    });

    // Widget events carry raw HTML (listing cards, viewing forms, video tours).
    // A widget may embed an <input name="messages"> hidden field carrying a JSON
    // ConversationMessage — e.g. ListingCarousel ships the listings payload this
    // way so the LLM has context for follow-up questions about what it returned.
    // Harvest any such inputs into the conversation array, then strip them.
    es.addEventListener("widget", (e: Event) => {
      widgetsEl.insertAdjacentHTML("beforeend", (e as MessageEvent).data as string);
      widgetsEl.querySelectorAll<HTMLInputElement>('input[name="messages"]').forEach((input) => {
        try {
          const parsed = JSON.parse(input.value) as ConversationMessage;
          if ((parsed.role === "user" || parsed.role === "assistant") && typeof parsed.content === "string") {
            conversation.push(parsed);
          }
        } catch (err) {
          console.error("search: widget context parse error", err);
        }
        input.remove();
      });
      scrollToBottom();
    });

    // Timings event carries rendered HTML
    es.addEventListener("timings", (e: Event) => {
      timingsEl.innerHTML = (e as MessageEvent).data as string;
    });

    // End — stream complete
    es.addEventListener("end", () => {
      close();
      setStatusText(container, "");

      // Persist assistant text to conversation history for multi-turn context
      const finalText = textEl.dataset.raw ?? "";
      if (finalText) {
        conversation.push({role: "assistant", content: finalText});
      }

      finishSubmit();
      scrollToBottom();
    });

    es.onerror = () => {
      close();
      setStreamError(container, "Connection lost. Please try again.");
      finishSubmit();
    };
  }

  // ── Scroll ────────────────────────────────────────────────────────────────
  // The page now scrolls the document body (the inner-scroll trick was incompatible
  // with the Next.js wrapper, which doesn't pass a definite height to the embed).
  function scrollToBottom(): void {
    requestAnimationFrame(() => {
      window.scrollTo({top: document.documentElement.scrollHeight, behavior: "auto"});
    });
  }

  // ── Per-tile "Book viewing" button ───────────────────────────────────────
  // Sends a natural-language user message so the LLM kicks off its existing
  // viewing flow (getViewingSlots → sendBookingPicker → ...).
  pageEl.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>('button[data-action="book-viewing"]');
    if (!btn || btn.disabled) {
      return;
    }
    e.preventDefault();
    const reference = btn.dataset.listingReference || "";
    const address = btn.dataset.listingAddress || "";
    const label = address ? `${address}${reference ? ` (${reference})` : ""}` : reference;
    if (!label) {
      return;
    }
    btn.disabled = true;
    textareaEl.value = `I'd like to book a viewing for ${label}.`;
    syncTextarea();
    submit();
  });

  // ── Rail controls (listing carousel prev/next) ───────────────────────────
  // Carousels are injected dynamically into assistant bubbles, so wire via delegation.
  // The scroller uses `snap-center snap-always`, so the real snap point for a tile is
  // `offsetLeft + width/2 - viewport/2`, not `offsetLeft`. Edge tiles clamp to 0 / max.
  pageEl.addEventListener("click", (e) => {
    const target = (e.target as HTMLElement | null)?.closest<HTMLElement>("[data-rail-prev],[data-rail-next]");
    if (!target) {
      return;
    }
    const rail = target.closest<HTMLElement>("[data-listing-rail]");
    const scroller = rail?.querySelector<HTMLElement>("[data-rail-scroller]");
    if (!scroller) {
      return;
    }
    const tiles = Array.from(scroller.children).filter((c): c is HTMLElement => c instanceof HTMLElement);
    if (!tiles.length) {
      return;
    }
    const isPrev = target.matches("[data-rail-prev]");
    const viewport = scroller.clientWidth;
    const maxScroll = Math.max(0, scroller.scrollWidth - viewport);

    const snaps = Array.from(
      new Set(tiles.map((t) => Math.max(0, Math.min(maxScroll, t.offsetLeft + t.offsetWidth / 2 - viewport / 2)))),
    ).sort((a, b) => a - b);

    const current = scroller.scrollLeft;
    const tolerance = 2;
    // Only consider snap points strictly in the requested direction — guarantees
    // progress on every click and lands on a real snap point so snap-mandatory
    // doesn't override us.
    const candidates = isPrev
      ? snaps.filter((s) => s < current - tolerance)
      : snaps.filter((s) => s > current + tolerance);
    if (!candidates.length) {
      return;
    }
    const desired = current + (isPrev ? -viewport * 0.85 : viewport * 0.85);
    const next = candidates.reduce((best, s) => (Math.abs(s - desired) < Math.abs(best - desired) ? s : best));
    scroller.scrollTo({left: next, behavior: "smooth"});
  });

  // ── In-chat viewing booking widgets ──────────────────────────────────────
  // Each widget is a server-rendered <form data-widget-form="..."> injected
  // into the assistant bubble. On submit, we read the form, lock the inputs,
  // and push a structured user message back through the chat composer so the
  // LLM advances the booking flow (picker → applicant form → request-details
  // form → bookViewing).
  type WidgetPayload =
    | {action: "slots-selected"; listingId: string; isos: Array<string>}
    | {
        action: "applicant-info";
        listingId: string;
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        applicantType: string;
      }
    | {
        action: "request-details";
        listingId: string;
        moveInDate: string;
        tenantCount: number;
        cotenantType?: string;
        employmentStatus: string;
        annualIncome: number;
        tenantsWithAdverseCredit: string;
        failedIncomeTestPaymentOption?: "UK_GUARANTOR" | "SCRAYE_GUARANTOR" | "SCRAYE_GUARANTOR_ENHANCED";
      };

  function lockWidget(form: HTMLFormElement): void {
    form.dataset.locked = "1";
    form
      .querySelectorAll<HTMLInputElement | HTMLButtonElement | HTMLSelectElement | HTMLTextAreaElement>(
        "input, button, select, textarea",
      )
      .forEach((el) => {
        el.disabled = true;
      });
  }

  function sendWidgetPayload(payload: WidgetPayload): void {
    submit(`[chat-viewing] ${JSON.stringify(payload)}`);
  }

  function updatePickerSubmitState(form: HTMLFormElement): void {
    const required = Number.parseInt(form.dataset.requiredSlots || "1", 10);
    const requiredDays = Number.parseInt(form.dataset.requiredDays || "1", 10);
    const checked = Array.from(form.querySelectorAll<HTMLInputElement>('input[name="iso"]:checked'));
    const submitBtn = form.querySelector<HTMLButtonElement>("[data-widget-submit]");
    if (!submitBtn) {
      return;
    }
    const days = new Set(checked.map((c) => new Date(c.value).toISOString().slice(0, 10)));
    submitBtn.disabled = !(checked.length === required && days.size >= requiredDays);
  }

  function submitBookingPicker(form: HTMLFormElement): void {
    if (form.dataset.locked) {
      return;
    }
    const required = Number.parseInt(form.dataset.requiredSlots || "1", 10);
    const requiredDays = Number.parseInt(form.dataset.requiredDays || "1", 10);
    const checked = Array.from(form.querySelectorAll<HTMLInputElement>('input[name="iso"]:checked'));
    if (checked.length !== required) {
      return;
    }
    const days = new Set(checked.map((c) => new Date(c.value).toISOString().slice(0, 10)));
    if (days.size < requiredDays) {
      return;
    }
    const listingId = form.dataset.listingId || "";
    const isos = checked.map((c) => c.value);
    lockWidget(form);
    sendWidgetPayload({action: "slots-selected", listingId, isos});
  }

  function submitApplicantForm(form: HTMLFormElement): void {
    if (form.dataset.locked) {
      return;
    }
    if (!form.reportValidity()) {
      return;
    }
    const data = new FormData(form);
    const firstName = String(data.get("firstName") || "").trim();
    const lastName = String(data.get("lastName") || "").trim();
    const email = String(data.get("email") || "").trim();
    const phone = String(data.get("phone") || "").trim();
    const applicantType = String(data.get("applicantType") || "").trim();
    if (!firstName || !lastName || !email || !phone || !applicantType) {
      return;
    }
    const listingId = form.dataset.listingId || "";
    lockWidget(form);
    sendWidgetPayload({action: "applicant-info", listingId, firstName, lastName, email, phone, applicantType});
  }

  function submitRequestDetailsForm(form: HTMLFormElement): void {
    if (form.dataset.locked) {
      return;
    }
    if (!form.reportValidity()) {
      return;
    }
    const data = new FormData(form);
    const moveInDate = String(data.get("moveInDate") || "").trim();
    const tenantCount = Number.parseInt(String(data.get("tenantCount") || ""), 10);
    const cotenantTypeRaw = String(data.get("cotenantType") || "").trim();
    const employmentStatus = String(data.get("employmentStatus") || "").trim();
    const annualIncome = Number.parseFloat(String(data.get("annualIncome") || ""));
    const tenantsWithAdverseCredit = String(data.get("tenantsWithAdverseCredit") || "").trim();
    if (
      !moveInDate ||
      !Number.isFinite(tenantCount) ||
      tenantCount < 1 ||
      !employmentStatus ||
      !Number.isFinite(annualIncome) ||
      annualIncome < 0 ||
      !tenantsWithAdverseCredit
    ) {
      return;
    }
    if (tenantCount >= 3 && !cotenantTypeRaw) {
      return;
    }
    const requiredIncome = Number.parseFloat(form.dataset.requiredIncomePounds || "0");
    const gateActive = isAffordabilityGateActive(
      annualIncome,
      tenantsWithAdverseCredit,
      employmentStatus,
      requiredIncome,
    );
    const paymentOptionRaw = String(data.get("failedIncomeTestPaymentOption") || "").trim();
    const paymentOption =
      paymentOptionRaw === "UK_GUARANTOR" ||
      paymentOptionRaw === "SCRAYE_GUARANTOR" ||
      paymentOptionRaw === "SCRAYE_GUARANTOR_ENHANCED"
        ? paymentOptionRaw
        : undefined;
    if (gateActive && !paymentOption) {
      return;
    }
    const listingId = form.dataset.listingId || "";
    lockWidget(form);
    sendWidgetPayload({
      action: "request-details",
      listingId,
      moveInDate,
      tenantCount,
      ...(tenantCount >= 3 && cotenantTypeRaw ? {cotenantType: cotenantTypeRaw} : {}),
      employmentStatus,
      annualIncome,
      tenantsWithAdverseCredit,
      ...(gateActive && paymentOption ? {failedIncomeTestPaymentOption: paymentOption} : {}),
    });
  }

  // Affordability-gate trigger: mirrors the listing-page BookViewingForm —
  // income below 2.5× annual rent OR any adverse credit OR unemployed.
  function isAffordabilityGateActive(
    annualIncome: number,
    tenantsWithAdverseCredit: string,
    employmentStatus: string,
    requiredIncome: number,
  ): boolean {
    const incomeLow =
      Number.isFinite(annualIncome) &&
      Number.isFinite(requiredIncome) &&
      requiredIncome > 0 &&
      annualIncome < requiredIncome;
    const credit = tenantsWithAdverseCredit && tenantsWithAdverseCredit !== "NO";
    const unemployed = employmentStatus === "UNEMPLOYED";
    return !!(incomeLow || credit || unemployed);
  }

  function updateCotenantVisibility(form: HTMLFormElement): void {
    const tenantCountSelect = form.querySelector<HTMLSelectElement>('select[name="tenantCount"]');
    const cotenantBlock = form.querySelector<HTMLElement>("[data-cotenant-block]");
    const cotenantSelect = form.querySelector<HTMLSelectElement>('select[name="cotenantType"]');
    if (!tenantCountSelect || !cotenantBlock || !cotenantSelect) {
      return;
    }
    const count = Number.parseInt(tenantCountSelect.value, 10);
    const visible = Number.isFinite(count) && count >= 3;
    cotenantBlock.classList.toggle("hidden", !visible);
    cotenantSelect.required = visible;
    if (!visible) {
      cotenantSelect.value = "";
    }
  }

  // Affordability gate: mirrors listing-page BookViewingForm — shows when the
  // entered income is below 2.5× annual rent, OR adverseCredit is non-NO, OR
  // employmentStatus is UNEMPLOYED. The applicant must pick a payment option
  // (UK guarantor / Scraye guarantor) before the submit button unlocks.
  // Scraye Guarantor options are only available when activeCCJs is false and
  // the listing has the corresponding scrayeGuarantor mode (STANDARD/ENHANCED).
  function updateAffordabilityGate(form: HTMLFormElement): void {
    const gate = form.querySelector<HTMLElement>("[data-affordability-gate]");
    const submitBtn = form.querySelector<HTMLButtonElement>("[data-widget-submit]");
    if (!gate || !submitBtn) {
      return;
    }
    const incomeInput = form.querySelector<HTMLInputElement>('input[name="annualIncome"]');
    const creditSelect = form.querySelector<HTMLSelectElement>('select[name="tenantsWithAdverseCredit"]');
    const employmentSelect = form.querySelector<HTMLSelectElement>('select[name="employmentStatus"]');
    const requiredIncome = Number.parseFloat(form.dataset.requiredIncomePounds || "0");
    const annualIncome = Number.parseFloat(incomeInput?.value || "");
    const creditValue = (creditSelect?.value || "").trim();
    const employmentValue = (employmentSelect?.value || "").trim();
    const gateActive = isAffordabilityGateActive(annualIncome, creditValue, employmentValue, requiredIncome);

    gate.classList.toggle("hidden", !gateActive);
    gate.classList.toggle("flex", gateActive);

    const activeCCJs = creditValue === "ACTIVE_CCJ";
    const scrayeStandardAllowed = gate.dataset.scrayeStandard === "1" && !activeCCJs;
    const scrayeEnhancedAllowed = gate.dataset.scrayeEnhanced === "1" && !activeCCJs;
    const standardBlock = form.querySelector<HTMLElement>("[data-scraye-standard-option]");
    const enhancedBlock = form.querySelector<HTMLElement>("[data-scraye-enhanced-option]");
    if (standardBlock) {
      standardBlock.classList.toggle("hidden", !scrayeStandardAllowed);
    }
    if (enhancedBlock) {
      enhancedBlock.classList.toggle("hidden", !scrayeEnhancedAllowed);
    }

    const radios = Array.from(form.querySelectorAll<HTMLInputElement>('input[name="failedIncomeTestPaymentOption"]'));
    if (!gateActive) {
      radios.forEach((r) => (r.checked = false));
      syncChoiceCardStates(form);
      submitBtn.disabled = false;
      return;
    }
    // If the user previously selected a Scraye option but then declared active
    // CCJs, clear that selection so they can't submit a now-disallowed choice.
    radios.forEach((r) => {
      if (r.checked) {
        if (r.value === "SCRAYE_GUARANTOR" && !scrayeStandardAllowed) {
          r.checked = false;
        }
        if (r.value === "SCRAYE_GUARANTOR_ENHANCED" && !scrayeEnhancedAllowed) {
          r.checked = false;
        }
      }
    });
    syncChoiceCardStates(form);
    const selected = radios.find((r) => r.checked);
    submitBtn.disabled = !selected;
  }

  // Mirror each payment-choice radio's checked state onto its label via a
  // data-checked attribute so the card's selected styling is driven entirely
  // from JS (avoids relying on :has() / peer-checked, which behave
  // inconsistently across the codebase's Tailwind setup).
  function syncChoiceCardStates(form: HTMLFormElement): void {
    form.querySelectorAll<HTMLInputElement>('input[name="failedIncomeTestPaymentOption"]').forEach((input) => {
      const label = input.closest<HTMLLabelElement>("[data-payment-choice]");
      if (label) {
        label.dataset.checked = input.checked ? "true" : "false";
      }
    });
  }

  // Booking picker: keep the confirm button enabled state in sync with selection.
  // Request-details form: reveal the co-tenant select when tenantCount ≥ 3,
  // and toggle the affordability gate based on annualIncome / guarantor choice.
  pageEl.addEventListener("change", (e) => {
    const target = e.target as HTMLElement | null;
    const pickerForm = target?.closest<HTMLFormElement>('form[data-widget-form="chat-booking-picker"]');
    if (pickerForm && !pickerForm.dataset.locked) {
      updatePickerSubmitState(pickerForm);
      return;
    }
    const detailsForm = target?.closest<HTMLFormElement>('form[data-widget-form="chat-request-details-form"]');
    if (detailsForm && !detailsForm.dataset.locked) {
      if ((target as HTMLElement)?.matches?.('select[name="tenantCount"]')) {
        updateCotenantVisibility(detailsForm);
      }
      updateAffordabilityGate(detailsForm);
    }
  });

  pageEl.addEventListener("input", (e) => {
    const detailsForm = (e.target as HTMLElement | null)?.closest<HTMLFormElement>(
      'form[data-widget-form="chat-request-details-form"]',
    );
    if (!detailsForm || detailsForm.dataset.locked) {
      return;
    }
    const target = e.target as HTMLElement;
    if (target.matches?.('input[name="annualIncome"]')) {
      updateAffordabilityGate(detailsForm);
    }
  });

  pageEl.addEventListener("submit", (e) => {
    const form = (e.target as HTMLElement | null)?.closest<HTMLFormElement>("form[data-widget-form]");
    if (!form) {
      return;
    }
    e.preventDefault();
    switch (form.dataset.widgetForm) {
      case "chat-booking-picker": {
        submitBookingPicker(form);
        break;
      }
      case "chat-applicant-form": {
        submitApplicantForm(form);
        break;
      }
      case "chat-request-details-form": {
        submitRequestDetailsForm(form);
        break;
      }
    }
  });

  // ── Auto-submit from URL query param ─────────────────────────────────────
  const urlParams = new URLSearchParams(window.location.search);
  const initialQuery = urlParams.get("q");
  if (initialQuery?.trim()) {
    textareaEl.value = initialQuery.trim();
    syncTextarea();
    submit();
  } else {
    // Land directly in the composer — saves a click for the user's first message.
    textareaEl.focus({preventScroll: true});
  }
}

// Run immediately if the DOM is already ready, or wait for it.
// `document.currentScript` can be null for async/deferred scripts in some
// environments, so we fall back to DOMContentLoaded.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initSearch);
} else {
  initSearch();
}
